/**
 * Chat Error States
 *
 * Error handling and display components for the AI Chat interface.
 */

import React from 'react';
import { Box, Paper, Typography, Button, IconButton, Alert, AlertTitle, Collapse } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';

/**
 * Error Message Bubble - Inline error display within chat
 */
interface ErrorMessageBubbleProps {
  message?: string;
  onRetry?: () => void;
  onDismiss?: () => void;
}

export const ErrorMessageBubble: React.FC<ErrorMessageBubbleProps> = ({
  message,
  onRetry,
  onDismiss,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <Box className={classes.errorBubbleContainer}>
      <Paper className={classes.errorBubble}>
        <Box className={classes.errorBubbleContent}>
          <ErrorOutlineIcon className={classes.errorIcon} />
          <Box className={classes.errorTextContainer}>
            <Typography variant="body2" className={classes.errorText}>
              {message || t('aiChatError', 'Something went wrong')}
            </Typography>
          </Box>
          {onDismiss && (
            <IconButton size="small" onClick={onDismiss} className={classes.dismissButton}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        {onRetry && (
          <Button
            size="small"
            variant="text"
            startIcon={<RefreshIcon />}
            onClick={onRetry}
            className={classes.retryButton}
          >
            {t('aiChatRetry', 'Try again')}
          </Button>
        )}
      </Paper>
    </Box>
  );
};

/**
 * Connection Error - When unable to reach the AI service
 */
interface ConnectionErrorProps {
  onRetry?: () => void;
}

export const ConnectionError: React.FC<ConnectionErrorProps> = ({ onRetry }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <Box className={classes.connectionError}>
      <WifiOffIcon className={classes.connectionIcon} />
      <Typography variant="subtitle1" className={classes.connectionTitle}>
        {t('aiChatNoConnection', 'Unable to connect to the AI assistant')}
      </Typography>
      <Typography variant="body2" color="textSecondary" className={classes.connectionText}>
        {t('aiChatCheckConnection', 'Please check your internet connection and try again.')}
      </Typography>
      {onRetry && (
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={onRetry}
          className={classes.connectionRetryButton}
        >
          {t('aiChatRetry', 'Try again')}
        </Button>
      )}
    </Box>
  );
};

/**
 * Toast Error - Dismissible error notification
 */
interface ToastErrorProps {
  message: string;
  open: boolean;
  onClose: () => void;
  severity?: 'error' | 'warning';
  details?: string;
}

export const ToastError: React.FC<ToastErrorProps> = ({
  message,
  open,
  onClose,
  severity = 'error',
  details,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = React.useState(false);

  if (!open) return null;

  return (
    <Box className={classes.toastContainer}>
      <Alert
        severity={severity}
        onClose={onClose}
        className={classes.toastAlert}
        action={
          <Box display="flex" alignItems="center">
            {details && (
              <IconButton
                size="small"
                onClick={() => setShowDetails(!showDetails)}
                className={classes.detailsToggle}
              >
                {showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            )}
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        }
      >
        <AlertTitle>{severity === 'error' ? t('error', 'Error') : t('warning', 'Warning')}</AlertTitle>
        {message}
        {details && (
          <Collapse in={showDetails}>
            <Typography variant="caption" className={classes.errorDetails}>
              {details}
            </Typography>
          </Collapse>
        )}
      </Alert>
    </Box>
  );
};

/**
 * Empty State Error - When chat cannot load
 */
interface EmptyStateErrorProps {
  title?: string;
  message?: string;
  onRetry?: () => void;
}

export const EmptyStateError: React.FC<EmptyStateErrorProps> = ({
  title,
  message,
  onRetry,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <Box className={classes.emptyStateError}>
      <ErrorOutlineIcon className={classes.emptyStateIcon} />
      <Typography variant="h6" className={classes.emptyStateTitle}>
        {title || t('aiChatError', 'Something went wrong')}
      </Typography>
      <Typography variant="body2" color="textSecondary">
        {message || t('aiChatLoadError', 'We encountered an error while loading the chat. Please try again.')}
      </Typography>
      {onRetry && (
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={onRetry}
          className={classes.emptyStateButton}
        >
          {t('aiChatRetry', 'Try again')}
        </Button>
      )}
    </Box>
  );
};

/**
 * Rate Limit Error - When API rate limit is exceeded
 */
interface RateLimitErrorProps {
  retryAfter?: number; // seconds
}

export const RateLimitError: React.FC<RateLimitErrorProps> = ({ retryAfter }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [countdown, setCountdown] = React.useState(retryAfter || 60);

  React.useEffect(() => {
    if (countdown <= 0) return;

    const timer = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [countdown]);

  return (
    <Box className={classes.rateLimitContainer}>
      <Alert severity="warning" className={classes.rateLimitAlert}>
        <AlertTitle>Slow down</AlertTitle>
        <Typography variant="body2">
          {t('aiChatRateLimited', 'You\'re sending messages too quickly. Please wait {{seconds}} seconds before trying again.', { seconds: countdown })}
        </Typography>
      </Alert>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  // Error Message Bubble
  errorBubbleContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(1),
  },
  errorBubble: {
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.error.main + '10',
    borderRadius: 12,
    border: `1px solid ${theme.palette.error.main}30`,
    maxWidth: '90%',
  },
  errorBubbleContent: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  errorIcon: {
    color: theme.palette.error.main,
    fontSize: '1.25rem',
    flexShrink: 0,
    marginTop: 2,
  },
  errorTextContainer: {
    flex: 1,
  },
  errorText: {
    color: theme.palette.error.dark,
  },
  dismissButton: {
    marginTop: -4,
    marginRight: -4,
    color: theme.palette.error.main,
  },
  retryButton: {
    marginTop: theme.spacing(1),
    color: theme.palette.error.main,
  },

  // Connection Error
  connectionError: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4),
    textAlign: 'center',
    height: '100%',
  },
  connectionIcon: {
    fontSize: 48,
    color: theme.palette.grey[400],
    marginBottom: theme.spacing(2),
  },
  connectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  connectionText: {
    marginBottom: theme.spacing(3),
    maxWidth: 280,
  },
  connectionRetryButton: {
    borderRadius: 20,
  },

  // Toast Error
  toastContainer: {
    position: 'absolute',
    top: theme.spacing(1),
    left: theme.spacing(1),
    right: theme.spacing(1),
    zIndex: 1,
  },
  toastAlert: {
    boxShadow: theme.shadows[3],
  },
  detailsToggle: {
    marginRight: theme.spacing(0.5),
  },
  errorDetails: {
    display: 'block',
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[100],
    borderRadius: 4,
    fontFamily: 'monospace',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
  },

  // Empty State Error
  emptyStateError: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4),
    textAlign: 'center',
    height: '100%',
    gap: theme.spacing(1),
  },
  emptyStateIcon: {
    fontSize: 56,
    color: theme.palette.error.light,
    marginBottom: theme.spacing(1),
  },
  emptyStateTitle: {
    fontWeight: 600,
    color: theme.palette.error.main,
  },
  emptyStateButton: {
    marginTop: theme.spacing(2),
  },

  // Rate Limit
  rateLimitContainer: {
    padding: theme.spacing(1),
  },
  rateLimitAlert: {
    borderRadius: 12,
  },
}));

export default ErrorMessageBubble;

